import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./App.css";

const GAMMA_URL =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_GAMMA_URL) ||
  "https://transforming-access-s4uh62z.gamma.site/";

export default function App() {
  const [showFallback, setShowFallback] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  // If the site blocks embedding, we can't detect it cleanly,
  // but we can show a friendly fallback after a short delay.
  useEffect(() => {
    const t = setTimeout(() => setShowFallback(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsModalOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  // Submit to Netlify without navigation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsError(false);
    setIsSubmitting(true);

    const form = e.target;
    const formData = new FormData(form);
    // Ensure form-name is included for Netlify
    formData.set("form-name", "contact");

    try {
      const response = await fetch(window.location.pathname || "/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      if (!response.ok) throw new Error("Network error");
      setIsSuccess(true);
      form.reset();
    } catch (err) {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="shell">
      <iframe
        title="ApplyTN — Transforming Access"
        className="frame"
        style={{ pointerEvents: isModalOpen ? "none" : "auto", zIndex: 0 }}
        src={GAMMA_URL}
        loading="eager"
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-read; clipboard-write; fullscreen; accelerometer; gyroscope"
      />
      <div
        className="contact-button"
        style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
      >
        <button
          type="button"
          className="contact-link"
          onClick={() => {
            console.log("Contact button clicked");
            setIsModalOpen(true);
          }}
          style={{ pointerEvents: "auto" }}
          aria-haspopup="dialog"
          aria-expanded={isModalOpen}
        >
          Contact Us
        </button>
      </div>
      {/* Remove this if you don't want the link fallback */}
      {/* {showFallback && (
        <div className="fallback">
          <p>
            If the preview doesn’t load,{" "}
            <a href={GAMMA_URL} target="_blank" rel="noreferrer">
              open the ApplyTN preview in a new tab
            </a>
            .
          </p>
        </div>
      )} */}
      {isModalOpen &&
        createPortal(
          <div
            className="modal-overlay"
            role="presentation"
            onClick={() => setIsModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000
            }}
          >
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="contact-title"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(520px, 92vw)",
                background: "#ffffff",
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                padding: "20px 20px 16px",
                position: "relative",
                fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"
              }}
            >
              <button
                type="button"
                className="modal-close"
                aria-label="Close contact form"
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </button>
              <h2 id="contact-title">Contact ApplyTN</h2>
              <form
                name="contact"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                {isSuccess ? (
                  <>
                    <p role="status" aria-live="polite" style={{ fontSize: 16, margin: "8px 0" }}>
                      ✅ Thanks — we’ll be in touch shortly.
                    </p>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          setIsModalOpen(false);
                          setIsSuccess(false);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                      <label>
                        Don’t fill this out: <input name="bot-field" />
                      </label>
                    </p>

                    <label className="field">
                      Your name
                      <input type="text" name="name" placeholder="Jane Doe" required />
                    </label>

                    <label className="field">
                      Email
                      <input type="email" name="email" placeholder="you@example.com" required />
                    </label>

                    <label className="field">
                      Message
                      <textarea name="message" rows="5" placeholder="How can we help?" required></textarea>
                    </label>

                    {isError && (
                      <p role="alert" style={{ color: "#b00020", margin: "6px 0" }}>
                        Something went wrong. Please try again.
                      </p>
                    )}

                    <div className="actions">
                      <button type="submit" className="btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? "Sending…" : "Send"}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setIsModalOpen(false)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </form>
              <p className="privacy-note">
                We’ll use your email only to reply to this message.
              </p>
            </div>
          </div>,
          document.body
        )
      }
    </div>
  );
}